import { Fragment, ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  MenuItem,
  Tab,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { RateReview as RateReviewIcon, Download as DownloadIcon, Check as CheckIcon } from "@mui/icons-material";
import { InstanciaPaso } from "../../types/instanciaPaso";
import { AccionPostulacion, Postulacion } from "../../types/postulacion";
import { useMisPostulaciones, useResolverMiPostulacion } from "../../queries/usePostulaciones";
import { useAuthStore } from "../../stores/authStore";
import { TablaKit, FilaKit, CeldaKit, CeldaPrincipal, Columna } from "../kit/TablaKit";
import AvatarAmpliable from "../kit/AvatarAmpliable";
import Contador from "../kit/Contador";
import Correo from "../kit/Correo";
import EstadoChip from "../kit/EstadoChip";
import EnlaceTema from "../tema/EnlaceTema";
import { EvaluarDialog } from "./ComisionEvaluacion";
import Etiqueta from "../kit/Etiqueta";
import { usePestanaUrl } from "../../utils/estadoLista";
import {
  agruparStaff,
  FilaEstudiante,
  OpcionSemestre,
  ordenRevision,
  periodoVigente,
  RolEnProceso,
  sinEntregar,
} from "../../utils/agruparStaff";
import { estiloDeInstancia, iconoDeInstancia } from "../../utils/estados";
import { useIr } from "../../utils/enlaces";
import { Accesores, ordenar, useOrden } from "../../utils/orden";
import { tokens } from "../../theme";

const COL_ESTUDIANTES: (string | Columna)[] = [
  { label: "Estudiante", sortKey: "estudiante" },
  { label: "Mi rol", sortKey: "rol", width: 130 },
  { label: "Tema", sortKey: "tema" },
  { label: "Última etapa", sortKey: "etapa" },
  { label: "Estado", sortKey: "estado" },
];

const ACC_ESTUDIANTES: Accesores<FilaEstudiante> = {
  estudiante: (f) => f.estudiante,
  // Guía antes que coguía: es el rol con más peso sobre el proceso.
  rol: (f) => (f.roles.includes("guia") ? 0 : 1),
  tema: (f) => f.tema,
  etapa: (f) => f.etapa,
  estado: (f) => f.estadoDisplay,
};

const ETIQUETA_ROL: Record<RolEnProceso, { label: string; variante: "acento" | "marca" }> = {
  guia: { label: "Guía", variante: "acento" },
  coguia: { label: "Coguía", variante: "marca" },
};

/**
 * Selector de semestre. Va **dentro** de cada pestaña de trabajo (Mis estudiantes y
 * Revisiones) y no en Solicitudes: ahí lo que hay son postulaciones por resolver, que
 * no tienen historial que mirar hacia atrás.
 */
function SelectorSemestre({
  semestres,
  valor,
  onChange,
  etiqueta,
  cargando,
}: {
  semestres: OpcionSemestre[];
  valor: string;
  onChange: (v: string) => void;
  etiqueta: string;
  cargando: boolean;
}) {
  if (semestres.length < 2) return null;
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      {/* "En curso" es el valor vacío, y MUI trata el vacío como "sin elegir": sin
          `displayEmpty` la caja queda en blanco y parece que falta escoger algo. */}
      <TextField
        select
        size="small"
        label="Semestre"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">En curso</MenuItem>
        {semestres.map((s) => (
          <MenuItem key={s.valor} value={s.valor}>
            {s.label}
          </MenuItem>
        ))}
      </TextField>
      {/* Elegir semestre pide esa tanda al servidor. El indicador va aquí, junto al
          control que la disparó: la tabla anterior sigue abajo, atenuada, para no
          vaciar la pantalla en cada cambio. */}
      {cargando ? (
        <>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Cargando {etiqueta || "lo que está en curso"}…
          </Typography>
        </>
      ) : (
        valor && (
          <Typography variant="body2" color="text.secondary">
            Mostrando {etiqueta}, procesos terminados incluidos.
          </Typography>
        )
      )}
    </Stack>
  );
}

/** Con qué sombrero participo en los procesos de este estudiante. Pueden ser los dos:
 *  guiarlo en su memoria y haber sido coguía de otro proceso suyo. */
function RolesEnFila({ roles }: { roles: RolEnProceso[] }) {
  if (!roles.length) return <>—</>;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {roles.map((r) => (
        <Etiqueta key={r} variante={ETIQUETA_ROL[r].variante}>
          {ETIQUETA_ROL[r].label}
        </Etiqueta>
      ))}
    </Stack>
  );
}

// Revisiones se agrupan por etapa + cohorte (año/semestre); sin columna Etapa.
const COL_REVISIONES: (string | Columna)[] = [
  { label: "Estudiante", sortKey: "estudiante" },
  { label: "Tema", sortKey: "tema" },
  { label: "Mi revisión", sortKey: "revision" },
  { label: "Acciones", align: "right" },
];

const ACC_REVISIONES: Accesores<InstanciaPaso> = {
  estudiante: (it) => it.estudiante,
  tema: (it) => it.tema,
  // Ascendente deja primero las por evaluar —lo que el revisor viene a buscar—, después
  // las que esperan la entrega y al final las evaluadas.
  revision: (it) => ordenRevision(it),
};

interface GrupoRevision {
  key: string;
  label: string;
  ano: number;
  semestre: number;
  paso: string;
  items: InstanciaPaso[];
}

/** Agrupa las revisiones por etapa + cohorte (año y semestre), período más reciente primero. */
function agruparRevisiones(insts: InstanciaPaso[]): GrupoRevision[] {
  const map = new Map<string, GrupoRevision>();
  for (const it of insts) {
    const key = `${it.paso}|${it.ano}|${it.semestre}`;
    if (!map.has(key)) {
      map.set(key, { key, label: `${it.paso} · ${it.ano} ${it.semestre_display}`, ano: it.ano, semestre: it.semestre, paso: it.paso, items: [] });
    }
    map.get(key)!.items.push(it);
  }
  return [...map.values()].sort((a, b) => b.ano - a.ano || b.semestre - a.semestre || a.paso.localeCompare(b.paso));
}

function Vacio({ texto }: { texto: string }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
      {texto}
    </Typography>
  );
}

// Guías / Coguías: un estudiante por fila con su tema, última etapa y estado.
function TablaEstudiantes({ filas, vacio, onAbrir }: { filas: FilaEstudiante[]; vacio: string; onAbrir: (rut: number | null) => void }) {
  const { orden, onSort } = useOrden();
  if (!filas.length) return <Vacio texto={vacio} />;
  return (
    <TablaKit columnas={COL_ESTUDIANTES} orden={orden} onSort={onSort}>
      <TableBody>
        {ordenar(filas, ACC_ESTUDIANTES, orden).map((f) => (
          <FilaKit key={f.estudianteId} onClick={() => onAbrir(f.rut)}>
            <CeldaPrincipal maxWidth={220}>
              {/* El avatar va dentro de la celda del nombre, no en una columna
                  aparte: una imagen de 32 px no justifica ensanchar la tabla. Al
                  pasarle el mouse se amplía sobre el resto, sin mover la fila. */}
              <Stack direction="row" spacing={1.25} alignItems="center">
                <AvatarAmpliable src={f.foto} nombre={f.estudiante} />
                <Box sx={{ minWidth: 0 }}>{f.estudiante}</Box>
              </Stack>
            </CeldaPrincipal>
            <CeldaKit>
              <RolesEnFila roles={f.roles} />
            </CeldaKit>
            <CeldaKit maxWidth={340} atenuada={!f.tema}>
              <EnlaceTema id={f.temaId} titulo={f.tema} />
            </CeldaKit>
            <CeldaKit>
              {f.etapa}
              <Typography variant="caption" color="text.secondary" display="block">
                {f.periodoDisplay}
              </Typography>
            </CeldaKit>
            <CeldaKit>
              <EstadoChip label={f.estadoDisplay} {...estiloDeInstancia(f.estado)} icono={iconoDeInstancia(f.estado)} denso />
            </CeldaKit>
          </FilaKit>
        ))}
      </TableBody>
    </TablaKit>
  );
}

/**
 * Revisiones: una instancia por fila, con estado de mi revisión y acciones.
 *
 * Las filas van con `destacadaEnHover`, igual que Solicitudes. El fondo sólido de
 * `destacada` significa **seleccionada** en el resto de la app (ver Evaluadores), y acá
 * se estaba usando para marcar "te falta evaluarla": como casi todas lo están, la tabla
 * entera se veía seleccionada y distinta a las demás. Que falte evaluar ya lo dicen el
 * chip "Por revisar" y el botón "Evaluar" de cada fila.
 */
function TablaRevisiones({
  instancias,
  vacio,
  onEvaluar,
  onAbrir,
}: {
  instancias: InstanciaPaso[];
  vacio: string;
  onEvaluar: (it: InstanciaPaso) => void;
  onAbrir: (rut: number | null) => void;
}) {
  // Un único estado de orden para todos los grupos (etapa + cohorte); se aplica dentro de cada uno.
  const { orden, onSort } = useOrden();
  if (!instancias.length) return <Vacio texto={vacio} />;
  return (
    <TablaKit columnas={COL_REVISIONES} minWidth={760} orden={orden} onSort={onSort}>
      <TableBody>
        {agruparRevisiones(instancias).map((g) => (
          <Fragment key={g.key}>
            <TableRow>
              <TableCell
                colSpan={4}
                sx={{
                  bgcolor: tokens.tabla.cabeceraBg,
                  borderTop: `1px solid ${tokens.tabla.filaBorde}`,
                  color: tokens.tabla.celdaFuerte,
                  fontWeight: 600,
                  fontSize: 13.5,
                  padding: "10px 22px",
                }}
              >
                {g.label}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 400 }}>
                  ({g.items.length})
                </Typography>
              </TableCell>
            </TableRow>
            {ordenar(g.items, ACC_REVISIONES, orden).map((it) => (
              <FilaKit key={it.id} destacadaEnHover onClick={() => onAbrir(it.estudiante_rut)}>
                <CeldaPrincipal maxWidth={220}>{it.estudiante}</CeldaPrincipal>
                <CeldaKit maxWidth={320} atenuada={!it.tema}>
                  <EnlaceTema id={it.tema_id} titulo={it.tema} />
                </CeldaKit>
                <CeldaKit>
                  {/* Sin entregar: ya estoy en su comisión, pero todavía no hay nada que leer. */}
                  {!it.ya_evalue && sinEntregar(it) ? (
                    <EstadoChip label="Sin entregar" tono="neutro" denso />
                  ) : (
                    <EstadoChip
                      label={it.ya_evalue ? "Evaluado" : "Por revisar"}
                      tono={it.ya_evalue ? "exito" : "aviso"}
                      icono={it.ya_evalue ? "check" : undefined}
                      denso
                    />
                  )}
                </CeldaKit>
                <CeldaKit align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                    {it.documento && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<DownloadIcon />}
                        component="a"
                        href={it.documento}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Informe
                      </Button>
                    )}
                    {/* Sin entrega no se puede evaluar (el backend lo rechaza): no hay botón. */}
                    {(it.ya_evalue || !sinEntregar(it)) && (
                      <Button
                        size="small"
                        variant={it.ya_evalue ? "text" : "contained"}
                        startIcon={<RateReviewIcon />}
                        onClick={() => onEvaluar(it)}
                      >
                        {it.ya_evalue ? "Volver a evaluar" : "Evaluar"}
                      </Button>
                    )}
                  </Stack>
                </CeldaKit>
              </FilaKit>
            ))}
          </Fragment>
        ))}
      </TableBody>
    </TablaKit>
  );
}

// Solicitudes: estudiantes que piden que yo los guíe (postulaciones pendientes), con
// confirmación al aceptar (aceptar asigna el tema y rechaza al resto de postulantes).
// Anchos repartidos a mano (con `anchosFijos`): la columna del estudiante lleva
// su correo debajo del nombre y, al repartir por contenido, se quedaba estrecha y
// lo partía en dos líneas.
const COL_SOLICITUDES: (string | Columna)[] = [
  { label: "Estudiante", sortKey: "estudiante", width: "30%" },
  { label: "Tema", sortKey: "tema", width: "30%" },
  { label: "Mensaje", width: "18%" },
  { label: "Acciones", align: "right", width: "22%" }, // dos botones, no se recortan
];

const ACC_SOLICITUDES: Accesores<Postulacion> = {
  estudiante: (p) => p.estudiante_nombre,
  tema: (p) => p.tema,
};

/**
 * `pendientes` son las solicitudes de mis temas (guía o coguía). El coordinador
 * además puede pedir las de todo el sistema con "Ver todo", que es otra consulta.
 * Cada fila abre la ficha del tema; los botones de acción no la disparan.
 */
function TablaSolicitudes({ pendientes }: { pendientes: Postulacion[] }) {
  const { user } = useAuthStore();
  const esCoordinador = !!user?.es_coordinador_global;
  const [todas, setTodas] = useState(false);
  const { data: globales, isFetching } = useMisPostulaciones(esCoordinador && todas, true);
  const resolver = useResolverMiPostulacion();
  const [confirmar, setConfirmar] = useState<{ p: Postulacion; accion: AccionPostulacion } | null>(null);
  // Comentario del rechazo: viaja al correo del estudiante. Es opcional, pero un
  // "no" sin explicación no le dice qué hacer distinto la próxima vez.
  const [motivo, setMotivo] = useState("");
  const { orden, onSort } = useOrden();
  const ir = useIr();

  const filas = todas ? globales ?? [] : pendientes;

  const abrir = (p: Postulacion, accion: AccionPostulacion) => {
    setMotivo("");
    setConfirmar({ p, accion });
  };

  const ejecutar = () => {
    if (!confirmar) return;
    resolver.mutate(
      { id: confirmar.p.id, accion: confirmar.accion, motivo: confirmar.accion === "rechazar" ? motivo : "" },
      { onSettled: () => setConfirmar(null) },
    );
  };

  const alternarAlcance = esCoordinador && (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
      <Button size="small" variant="text" disabled={isFetching} onClick={() => setTodas((v) => !v)}>
        {todas ? "Ver solo las mías" : "Ver todo"}
      </Button>
    </Box>
  );

  if (!filas.length) {
    return (
      <>
        <Vacio texto={todas ? "No hay solicitudes pendientes en el sistema." : "No tienes solicitudes pendientes."} />
        {alternarAlcance}
      </>
    );
  }

  return (
    <>
      {resolver.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No se pudo resolver la solicitud.
        </Alert>
      )}
      <TablaKit columnas={COL_SOLICITUDES} minWidth={940} orden={orden} onSort={onSort} anchosFijos>
        <TableBody>
          {ordenar(filas, ACC_SOLICITUDES, orden).map((p) => (
            <FilaKit key={p.id} destacadaEnHover onClick={(e) => ir(e, `/app/temas/${p.tema_id}`)}>
              <CeldaPrincipal>
                {p.estudiante_nombre}
                <Box sx={{ mt: 0.5, fontWeight: 400 }}>
                  {p.estudiante_email ? (
                    <Correo correo={p.estudiante_email} />
                  ) : (
                    <Typography variant="caption" color="text.secondary" display="block">
                      —
                    </Typography>
                  )}
                </Box>
              </CeldaPrincipal>
              <CeldaKit atenuada={!p.tema}>
                {p.tema ?? "—"}
              </CeldaKit>
              <CeldaKit atenuada={!p.mensaje} lineas={3}>
                {p.mensaje || "—"}
              </CeldaKit>
              <CeldaKit align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                  <Button size="small" variant="contained" color="error" disabled={resolver.isPending} onClick={() => abrir(p, "rechazar")}>
                    Rechazar
                  </Button>
                  <Button size="small" variant="contained" startIcon={<CheckIcon />} disabled={resolver.isPending} onClick={() => abrir(p, "aceptar")}>
                    Aceptar
                  </Button>
                </Stack>
              </CeldaKit>
            </FilaKit>
          ))}
        </TableBody>
      </TablaKit>
      {alternarAlcance}

      <Dialog open={confirmar !== null} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmar?.accion === "aceptar" ? "Aceptar estudiante" : "Rechazar solicitud"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmar?.accion === "aceptar" ? (
              <>
                Vas a aceptar a <strong>{confirmar.p.estudiante_nombre}</strong> en «{confirmar.p.tema}». Esto lo asigna al
                tema y rechaza automáticamente al resto de postulantes.
              </>
            ) : (
              <>
                Vas a rechazar la solicitud de <strong>{confirmar?.p.estudiante_nombre}</strong>.
              </>
            )}
          </DialogContentText>
          {confirmar?.accion === "rechazar" && (
            <TextField
              label="Motivo (opcional)"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Por ejemplo: el tema necesita un curso que aún no has tomado."
              helperText="Se lo mandamos en el correo, con tu nombre."
              fullWidth
              multiline
              minRows={3}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={confirmar?.accion === "aceptar" ? "primary" : "error"}
            disabled={resolver.isPending}
            onClick={ejecutar}
          >
            {confirmar?.accion === "aceptar" ? "Aceptar" : "Rechazar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Vista de "Procesos" para profesor / revisor / coordinador: pestañas —Solicitudes,
 * Mis estudiantes, Revisiones—. "Solicitudes" aparece cuando hay postulaciones
 * pendientes por aceptar —o siempre, si es coordinador— y va primero, resaltada en
 * naranjo como Revisiones. Cada fila abre el detalle o la acción pertinente.
 *
 * Guiar y coguiar van en la misma pestaña: en las dos uno acompaña a un estudiante y
 * separarlas obligaba a mirar en dos partes para saber a quién se tiene. El rol va
 * como etiqueta en cada fila.
 *
 * El selector de semestre es la puerta al historial y aplica a las dos pestañas de
 * trabajo. Sin elegir nada se ve lo que está en curso, que es la pantalla del día a
 * día; al elegir un semestre se ve todo lo de ese semestre, terminado o no.
 */
export default function ProcesosStaff({
  instancias,
  postulaciones = [],
  semestres,
  semestre,
  onSemestre,
  cargando = false,
}: {
  instancias: InstanciaPaso[];
  postulaciones?: Postulacion[];
  semestres: OpcionSemestre[];
  semestre: string;
  onSemestre: (v: string) => void;
  cargando?: boolean;
}) {
  const vigente = useMemo(() => periodoVigente(semestres), [semestres]);
  const { mios, revisiones, porRevisar } = useMemo(() => agruparStaff(instancias, semestre, vigente), [instancias, semestre, vigente]);
  const pendientes = useMemo(() => postulaciones.filter((p) => p.estado === "pendiente"), [postulaciones]);
  const { user } = useAuthStore();
  // En la URL: entrar a un estudiante desde "Revisiones" y volver tiene que dejarte
  // en "Revisiones", no en la primera pestaña.
  const [tabKey, setTabKey] = usePestanaUrl("ver", ["mios", "revisiones", "solicitudes"]);
  const [evaluar, setEvaluar] = useState<InstanciaPaso | null>(null);
  const navigate = useNavigate();
  const abrir = (rut: number | null) => rut != null && navigate(`/app/procesos/${rut}`);
  const etiquetaSemestre = semestres.find((s) => s.valor === semestre)?.label ?? semestre;

  const paneles: { key: string; label: string; badge: ReactNode; contenido: ReactNode }[] = [
    {
      key: "mios",
      label: "Mis estudiantes",
      badge: <Contador n={mios.length} />,
      contenido: (
        <>
          <SelectorSemestre semestres={semestres} valor={semestre} onChange={onSemestre} etiqueta={etiquetaSemestre} cargando={cargando} />
          <TablaEstudiantes
            filas={mios}
            vacio={
              semestre
                ? `No acompañaste a nadie en ${etiquetaSemestre}.`
                : // Con historial pero sin nadie en curso la pestaña vacía parece un error:
                  // el selector que va arriba es la vía, así que se nombra.
                  `No guías ni coguías a ningún estudiante con proceso en curso ni aprobado este semestre.${
                    semestres.length ? " Elige un semestre para ver los de años anteriores." : ""
                  }`
            }
            onAbrir={abrir}
          />
        </>
      ),
    },
    {
      key: "revisiones",
      label: "Revisiones",
      badge: <Contador n={semestre ? revisiones.length : porRevisar} resaltar={!semestre} />,
      contenido: (
        <>
          <SelectorSemestre semestres={semestres} valor={semestre} onChange={onSemestre} etiqueta={etiquetaSemestre} cargando={cargando} />
          <TablaRevisiones
            instancias={revisiones}
            vacio={
              semestre
                ? `No evaluaste ningún proceso en ${etiquetaSemestre}.`
                : `No tienes revisiones pendientes.${semestres.length ? " Elige un semestre para ver las de años anteriores." : ""}`
            }
            onEvaluar={setEvaluar}
            onAbrir={abrir}
          />
        </>
      ),
    },
  ];
  // La pestaña "Solicitudes" va primero. Se muestra si hay pendientes propias y, para
  // el coordinador, siempre: es su entrada al "Ver todo" aunque no guíe ningún tema.
  if (pendientes.length > 0 || user?.es_coordinador_global) {
    paneles.unshift({
      key: "solicitudes",
      label: "Solicitudes",
      badge: <Contador n={pendientes.length} resaltar />,
      contenido: <TablaSolicitudes pendientes={pendientes} />,
    });
  }
  const activo = paneles.find((p) => p.key === tabKey) ?? paneles[0];

  return (
    <Box>
      {/* Desplazables: en un móvil las últimas —Revisiones, la que trae trabajo—
          quedaban fuera de la pantalla. */}
      <Tabs
        value={activo.key}
        onChange={(_, v) => setTabKey(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {paneles.map((p) => (
          <Tab
            key={p.key}
            value={p.key}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {p.label}
                {p.badge}
              </Box>
            }
          />
        ))}
      </Tabs>

      <Box sx={{ opacity: cargando ? 0.45 : 1, transition: "opacity .15s" }}>{activo.contenido}</Box>

      {evaluar && <EvaluarDialog it={evaluar} onClose={() => setEvaluar(null)} />}
    </Box>
  );
}
